import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { useMemo } from 'react'
import type { AuthClient } from '../auth/client'
import { SignInPage } from '../auth/SignInPage'
import { Button, EmptyState, Spinner } from '../design'
import { ForcedPasswordChange } from '../features/password/ForcedPasswordChange'
import { OutageScreen } from '../features/status/OutageScreen'
import { ReadyzProbe } from '../features/status/ReadyzProbe'
import { ServiceStatusProvider } from '../features/status/ServiceStatusProvider'
import { UpdatePrompt } from '../features/status/UpdatePrompt'
import { useServiceStatus, useServiceStatusReporter } from '../features/status/useServiceStatus'
import { createAppRouter } from '../router'
import { SESSION_COPY } from '../session/copy'
import { NoAccessScreen } from '../session/NoAccessScreen'
import { SessionProvider } from '../session/SessionProvider'
import { useSession } from '../session/useSession'

const Routed = ({ auth }: { auth: AuthClient }) => {
  const session = useSession()
  const { outage, lastSyncedAt } = useServiceStatus()
  const router = useMemo(() => createAppRouter(), [])
  const probe = <ReadyzProbe api={session.api} />
  if (outage !== null)
    return (
      <>
        {probe}
        <OutageScreen entry={outage} lastSyncedAt={lastSyncedAt} />
      </>
    )
  if (session.status === 'loading') return <Spinner label={SESSION_COPY.loading} />
  if (session.status === 'no_access') return <NoAccessScreen onSignOut={() => void session.signOut()} />
  if (session.status === 'error') {
    return (
      <EmptyState
        title={SESSION_COPY.errorTitle}
        description={session.message}
        action={
          <Button variant="primary" onClick={session.retry}>
            {SESSION_COPY.retry}
          </Button>
        }
      />
    )
  }
  if (session.status === 'signed_out') return <SignInPage auth={auth} />
  if (session.status === 'password_change_required') return <ForcedPasswordChange />
  return <RouterProvider router={router} />
}

export interface AppProps {
  auth: AuthClient
  baseUrl: string
  requestedTenantId?: string | null
}

const Inner = ({ auth, baseUrl, requestedTenantId = null }: AppProps) => {
  const report = useServiceStatusReporter()
  return (
<>
      <UpdatePrompt />
      <SessionProvider auth={auth} baseUrl={baseUrl} requestedTenantId={requestedTenantId} onSignal={report}>
        <Routed auth={auth} />
      </SessionProvider>
    </>
  )
}

export const App = ({ auth, baseUrl, requestedTenantId = null }: AppProps) => {
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { staleTime: 5_000, retry: 1, refetchOnWindowFocus: true } } }),
    [],
  )
  return (
    <QueryClientProvider client={queryClient}>
      <ServiceStatusProvider>
        <Inner auth={auth} baseUrl={baseUrl} requestedTenantId={requestedTenantId} />
      </ServiceStatusProvider>
    </QueryClientProvider>
  )
}
